import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { HelmetProvider } from "react-helmet-async";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsOfService from "./pages/TermsOfService";
import DisclosurePolicy from "./pages/DisclosurePolicy";
import Disclaimer from "./pages/Disclaimer";
import BlogPost from "./pages/BlogPost";
import Unsubscribe from "./pages/Unsubscribe";
import ReportIssue from "./pages/ReportIssue";
import RequestBlog from "./pages/RequestBlog";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop";
import AdScript from "./components/AdScript";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import BlogPostsPage from "./pages/admin/BlogPosts";
import EditBlogPost from "./pages/admin/EditBlogPost";
import CategoriesPage from "./pages/admin/Categories";
import ShareAnalytics from "./pages/admin/ShareAnalytics";
import QuizManager from "./pages/admin/QuizManager";
import BulkQuizGenerator from "./pages/admin/BulkQuizGenerator";
import NewsletterManager from "./pages/admin/NewsletterManager";
import IssueReports from "./pages/admin/IssueReports";
import BlogRequests from "./pages/admin/BlogRequests";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import RoadmapOverview from "./pages/RoadmapOverview";
import RoadmapDayPage from "./pages/RoadmapDayPage";
import AdRedirect from "./pages/AdRedirect";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <HelmetProvider>
        <TooltipProvider>
          <BrowserRouter>
          <Toaster />
          <Sonner />
          <ScrollToTop />
          <BackToTop />
          <AdScript />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/disclosure" element={<DisclosurePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog/:slug/roadmap" element={<RoadmapOverview />} />
            <Route path="/blog/:slug/roadmap/day/:dayNumber" element={<RoadmapDayPage />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/report-issue" element={<ReportIssue />} />
            <Route path="/request-blog" element={<RequestBlog />} />
            <Route path="/ad-redirect" element={<AdRedirect />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/posts"
              element={
                <ProtectedRoute>
                  <BlogPostsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/posts/:id"
              element={
                <ProtectedRoute>
                  <EditBlogPost />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <ProtectedRoute>
                  <CategoriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <ShareAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/quiz/:blogPostId"
              element={
                <ProtectedRoute>
                  <QuizManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bulk-quiz-generator"
              element={
                <ProtectedRoute>
                  <BulkQuizGenerator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/newsletter"
              element={
                <ProtectedRoute>
                  <NewsletterManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/issue-reports"
              element={
                <ProtectedRoute>
                  <IssueReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog-requests"
              element={
                <ProtectedRoute>
                  <BlogRequests />
                </ProtectedRoute>
              }
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
      </HelmetProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
